import React, { useEffect, useMemo, useState } from 'react';
import {
 Video,
 Sparkles,
 Image as ImageIcon,
 Clock3,
 Download,
 Play,
 Settings2,
 Gem,
 PanelsTopLeft,
 CheckCircle2,
 SlidersHorizontal,
 RefreshCw,
 Type,
 Wand2
} from 'lucide-react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import LoginModal from './LoginModal';

const GENERATION_TYPES = [
 { id: 'text-to-video', label: '文生视频', desc: '输入提示词直接生成视频', icon: Type },
 { id: 'image-to-video', label: '图像生视频', desc: '上传图片后生成动态效果', icon: ImageIcon }
];

const MODEL_CONFIGS = [
 {
 id: 'sora-2',
 label: 'sora-2',
 desc: '基础版，支持 10 秒 / 15 秒，横竖屏',
 options: [
 { seconds: 10, size: '720x1280' },
 { seconds: 10, size: '1280x720' },
 { seconds: 15, size: '720x1280' },
 { seconds: 15, size: '1280x720' }
 ]
 },
 {
 id: 'sora-2-pro',
 label: 'sora-2-pro',
 desc: '高阶版，支持更多高分辨率尺寸',
 options: [
 { seconds: 10, size: '720x1280' },
 { seconds: 10, size: '1280x720' },
 { seconds: 10, size: '1024x1792' },
 { seconds: 10, size: '1792x1024' },
 { seconds: 15, size: '720x1280' },
 { seconds: 15, size: '1280x720' },
 { seconds: 15, size: '1024x1792' },
 { seconds: 15, size: '1792x1024' }
 ]
 }
];

// aspect 为宽高比（数字），用于 style={{ aspectRatio }}，实现不同高度的瀑布流
const SHOWCASE_VIDEOS = [
 {
 id: 1,
 prompt: '雨夜霓虹街头，女生撑伞回头看镜头，电影感慢推镜',
 gradient: 'from-accent-900 via-accent-800 to-accent-700',
 radial: 'radial-gradient(circle at 30% 40%, rgba(168,85,247,0.4), transparent 60%)',
 duration: '15s',
 model: 'sora-2-pro',
 aspect: 16 / 9
 },
 {
 id: 2,
 prompt: '微缩施工团队在巨型汉堡上搭建脚手架，移轴摄影质感',
 gradient: 'from-accent-700 via-accent-600 to-accent-500',
 radial: 'radial-gradient(circle at 70% 30%, rgba(251,191,36,0.5), transparent 55%)',
 duration: '10s',
 model: 'sora-2',
 aspect: 3 / 4
 },
 {
 id: 3,
 prompt: '海底珊瑚礁中穿梭的热带鱼群，阳光从水面透射下来',
 gradient: 'from-accent-800 via-accent-600 to-green-500',
 radial: 'radial-gradient(circle at 50% 20%, rgba(20,184,166,0.5), transparent 55%)',
 duration: '15s',
 model: 'sora-2-pro',
 aspect: 4 / 3
 },
 {
 id: 4,
 prompt: '赛博朋克城市天际线，飞行汽车穿梭霓虹楼宇之间',
 gradient: 'from-accent-900 via-accent-700 to-accent-500',
 radial: 'radial-gradient(circle at 80% 60%, rgba(236,72,153,0.4), transparent 55%)',
 duration: '10s',
 model: 'sora-2',
 aspect: 3 / 4
 },
 {
 id: 5,
 prompt: '航拍冰岛黑沙滩，海浪拍打岸边，无人机缓慢上升',
 gradient: 'from-ink-800 via-ink-600 to-accent-400',
 radial: 'radial-gradient(circle at 40% 70%, rgba(96,165,250,0.4), transparent 55%)',
 duration: '15s',
 model: 'sora-2-pro',
 aspect: 16 / 9
 },
 {
 id: 6,
 prompt: '樱花树下的日式庭院，花瓣随风飘落，柔焦光斑',
 gradient: 'from-accent-400 via-accent-300 to-accent-200',
 radial: 'radial-gradient(circle at 60% 30%, rgba(255,255,255,0.5), transparent 50%)',
 duration: '10s',
 model: 'sora-2',
 aspect: 1
 },
 {
 id: 7,
 prompt: '极光下的北欧木屋，星空延时摄影，宁静治愈氛围',
 gradient: 'from-green-900 via-green-700 to-accent-400',
 radial: 'radial-gradient(circle at 50% 15%, rgba(34,211,238,0.4), transparent 55%)',
 duration: '15s',
 model: 'sora-2-pro',
 aspect: 4 / 3
 },
 {
 id: 8,
 prompt: '沙漠中的骆驼商队，日落金色光线，航拍跟拍镜头',
 gradient: 'from-accent-800 via-accent-600 to-accent-300',
 radial: 'radial-gradient(circle at 50% 40%, rgba(245,158,11,0.5), transparent 55%)',
 duration: '10s',
 model: 'sora-2',
 aspect: 3 / 4
 }
];

function ShowcaseCard({ item, onUsePrompt }) {
 return (
 <button
 onClick={() => onUsePrompt(item.prompt)}
 className="group relative w-full overflow-hidden rounded-xl text-left transition-all duration-300 hover:brightness-110 hover:shadow-xl"
 >
 <div
 style={{ aspectRatio: item.aspect }}
 className={`relative w-full bg-gradient-to-br ${item.gradient}`}
 >
 <div className="absolute inset-0" style={{ background: item.radial }} />

 <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100">
 <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/25 backdrop-blur-sm">
 <Play className="ml-0.5 h-5 w-5 text-white" />
 </span>
 </div>

 <div className="absolute left-2.5 top-2.5 flex items-center gap-1">
 <span className="rounded-full bg-black/35 px-2 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm">
 {item.model}
 </span>
 <span className="rounded-full bg-black/35 px-2 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm">
 {item.duration}
 </span>
 </div>

 <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/65 via-black/20 to-transparent px-3 pb-3 pt-8">
 <p className="line-clamp-2 text-[12px] leading-[1.4] text-white/95">{item.prompt}</p>
 </div>

 <div className="absolute inset-0 flex items-end justify-end p-2.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
 <span className="flex items-center gap-1 rounded-full bg-white/25 px-2 py-1 text-[10px] font-medium text-white backdrop-blur-sm">
 <Wand2 className="h-2.5 w-2.5" />
 用这个
 </span>
 </div>
 </div>
 </button>
 );
}

function ShowcaseGallery({ onUsePrompt }) {
 return (
 <div>
 <div className="mb-4 flex items-center justify-between">
 <div>
 <h2 className="text-xl font-semibold text-ink-900">效果展示</h2>
 <p className="mt-1 text-sm text-ink-500">点击任意卡片，即可填入对应提示词</p>
 </div>
 <div className="inline-flex items-center gap-1.5 rounded-full bg-accent-50 px-3 py-1.5 text-xs font-medium text-accent-700">
 <Sparkles className="h-3.5 w-3.5" />
 {SHOWCASE_VIDEOS.length} 个示例
 </div>
 </div>
 <div className="columns-2 gap-2 lg:columns-3">
 {SHOWCASE_VIDEOS.map(item => (
 <div key={item.id} className="mb-2 break-inside-avoid">
 <ShowcaseCard item={item} onUsePrompt={onUsePrompt} />
 </div>
 ))}
 </div>
 </div>
 );
}

function ResultCard({ item }) {
 return (
 <div className="overflow-hidden rounded-xl border border-ink-200 bg-cream-50 shadow-sm">
 <div className="relative aspect-video bg-gradient-to-br from-ink-900 via-ink-800 to-ink-700">
 <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(120,255,170,0.18),transparent_26%),radial-gradient(circle_at_80%_80%,rgba(126,180,255,0.18),transparent_25%)]" />
 <button className="absolute inset-0 flex items-center justify-center">
 <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/15 backdrop-blur">
 <Play className="ml-1 h-7 w-7 text-white" />
 </span>
 </button>
 <div className="absolute left-4 top-4 rounded-full bg-black/35 px-3 py-1 text-xs text-white/85 backdrop-blur">
 已生成
 </div>
 </div>
 <div className="p-4">
 <div className="mb-2 flex flex-wrap gap-2">
 <span className="rounded-full bg-accent-50 px-2.5 py-1 text-xs font-medium text-accent-700">{item.size}</span>
 <span className="rounded-full bg-cream-100 px-2.5 py-1 text-xs font-medium text-ink-700">{item.duration}</span>
 <span className="rounded-full bg-cream-100 px-2.5 py-1 text-xs font-medium text-ink-700">{item.model}</span>
 <span className="rounded-full bg-cream-100 px-2.5 py-1 text-xs font-medium text-ink-700">
 {item.type === 'image-to-video' ? '图像生视频' : '文生视频'}
 </span>
 </div>
 <p className="line-clamp-2 text-sm text-ink-600">{item.prompt}</p>
 <div className="mt-4 flex items-center justify-between">
 <span className="text-xs text-ink-400">刚刚生成</span>
 <button className="inline-flex items-center gap-1 rounded-full bg-ink-900 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-black">
 <Download className="h-3.5 w-3.5" />
 下载
 </button>
 </div>
 </div>
 </div>
 );
}

export default function AIVideoPage() {
 const { showLoginModal, requireAuth, closeLoginModal, handleLoginSuccess } = useAuthGuard();
 const [activeType, setActiveType] = useState('text-to-video');
 const [prompt, setPrompt] = useState('');
 const [selectedModel, setSelectedModel] = useState('sora-2');
 const [selectedDuration, setSelectedDuration] = useState(10);
 const [selectedSize, setSelectedSize] = useState('720x1280');
 const [isGenerating, setIsGenerating] = useState(false);
 const [generatedVideos, setGeneratedVideos] = useState([]);
 const [referenceImage, setReferenceImage] = useState(null);

 const canGenerate = prompt.trim().length > 0 && !isGenerating;
 const currentModel = useMemo(
 () => MODEL_CONFIGS.find(item => item.id === selectedModel) || MODEL_CONFIGS[0],
 [selectedModel]
 );
 const availableDurations = useMemo(
 () => [...new Set(currentModel.options.map(item => item.seconds))],
 [currentModel]
 );
 const availableSizes = useMemo(
 () => currentModel.options.filter(item => item.seconds === selectedDuration).map(item => item.size),
 [currentModel, selectedDuration]
 );
 const currentPrice = selectedDuration === 15 ? 0.8 : 0.6;

 useEffect(() => {
 if (!availableDurations.includes(selectedDuration)) {
 setSelectedDuration(availableDurations[0]);
 }
 if (!availableSizes.includes(selectedSize)) {
 setSelectedSize(availableSizes[0]);
 }
 }, [selectedModel, selectedDuration, selectedSize, availableDurations, availableSizes]);

 const handleGenerate = () => {
 if (!requireAuth()) return;
 if (!prompt.trim()) return;
 setIsGenerating(true);
 setTimeout(() => {
 setGeneratedVideos(prev => [
 {
 id: Date.now(),
 prompt,
 size: selectedSize,
 duration: `${selectedDuration}s`,
 model: currentModel.label,
 type: activeType
 },
 ...prev
 ]);
 setIsGenerating(false);
 }, 1800);
 };

 return (
 <>
 {/* 去掉外层 container 包裹，直接用 flex 实现双栏布局 */}
 <div className="route-surface video-page flex min-h-screen">

 {/* ── 左栏：第二侧边栏，贴紧左边缘，白色，全高 sticky ── */}
 <div className="video-controls w-[300px] flex-shrink-0 bg-cream-50 border-r border-ink-100">
 <div className="sticky top-0 h-screen overflow-y-auto">
 <div className="space-y-5 p-5">

 <div className="flex items-center gap-2 border-b border-ink-100 pb-4">
 <PanelsTopLeft className="h-4 w-4 text-accent-500" />
 <span className="text-sm font-semibold text-ink-900">生成设置</span>
 </div>

 <section>
 <div className="mb-2 flex items-center gap-2">
 <Settings2 className="h-4 w-4 text-accent-500" />
 <span className="text-sm font-semibold text-ink-900">模型</span>
 </div>
 <div className="space-y-2">
 {MODEL_CONFIGS.map(model => (
 <button
 key={model.id}
 onClick={() => setSelectedModel(model.id)}
 className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
 selectedModel === model.id
 ? 'border-accent-300 bg-accent-50'
 : 'border-ink-200 bg-cream-50 hover:border-accent-200 hover:bg-accent-50/40'
 }`}
 >
 <div className="flex items-center justify-between gap-3">
 <div>
 <div className="text-sm font-semibold text-ink-900">{model.label}</div>
 <div className="mt-1 text-xs text-ink-500">{model.desc}</div>
 </div>
 {selectedModel === model.id && (
 <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-accent-600" />
 )}
 </div>
 </button>
 ))}
 </div>
 </section>

 <section>
 <div className="mb-2 flex items-center gap-2">
 <Video className="h-4 w-4 text-accent-500" />
 <span className="text-sm font-semibold text-ink-900">生成方式</span>
 </div>
 <div className="grid grid-cols-2 gap-2">
 {GENERATION_TYPES.map(item => {
 const Icon = item.icon;
 return (
 <button
 key={item.id}
 onClick={() => setActiveType(item.id)}
 className={`rounded-2xl border p-3 text-left transition ${
 activeType === item.id
 ? 'border-accent-300 bg-accent-50'
 : 'border-ink-200 hover:border-accent-200 hover:bg-accent-50/40'
 }`}
 >
 <div className="mb-2 flex items-center gap-2">
 <Icon className="h-4 w-4 text-accent-600" />
 <span className="text-sm font-semibold text-ink-900">{item.label}</span>
 </div>
 <div className="text-xs leading-5 text-ink-500">{item.desc}</div>
 </button>
 );
 })}
 </div>
 </section>

 {activeType === 'image-to-video' && (
 <section>
 <label className="mb-2 block text-sm font-semibold text-ink-900">参考图片</label>
 <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-ink-300 bg-cream-100 px-4 py-6 text-center transition hover:border-accent-300 hover:bg-accent-50/40">
 <ImageIcon className="mb-2 h-6 w-6 text-ink-400" />
 <span className="text-sm font-medium text-ink-700">
 {referenceImage ? referenceImage.name : '点击上传图片'}
 </span>
 <span className="mt-1 text-xs text-ink-400">支持 JPG / PNG</span>
 <input
 type="file"
 accept="image/*"
 className="hidden"
 onChange={e => setReferenceImage(e.target.files?.[0] || null)}
 />
 </label>
 </section>
 )}

 <section>
 <label className="mb-2 block text-sm font-semibold text-ink-900">提示词（Prompt）</label>
 <textarea
 value={prompt}
 onChange={e => setPrompt(e.target.value)}
 rows={6}
 placeholder="例如：一个微缩施工团队在巨型汉堡上搭建脚手架，镜头慢慢推进，商业广告质感，细节丰富，柔和灯光。"
 className="w-full resize-none rounded-2xl border border-ink-200 bg-cream-100 px-4 py-3 text-sm text-ink-700 outline-none transition focus:border-accent-300 focus:bg-cream-50 focus:ring-4 focus:ring-accent-100"
 />
 <div className="mt-2 flex items-center justify-between text-xs text-ink-400">
 <span>建议 20 - 120 字</span>
 <span>{prompt.length}/500</span>
 </div>
 </section>

 <section>
 <div className="mb-2 flex items-center gap-2">
 <Clock3 className="h-4 w-4 text-accent-500" />
 <span className="text-sm font-semibold text-ink-900">时长</span>
 </div>
 <div className="grid grid-cols-2 gap-2">
 {availableDurations.map(duration => (
 <button
 key={duration}
 onClick={() => setSelectedDuration(duration)}
 className={`rounded-2xl border px-3 py-3 text-left text-sm font-semibold transition ${
 selectedDuration === duration
 ? 'border-accent-300 bg-accent-50'
 : 'border-ink-200 hover:border-accent-200 hover:bg-accent-50/40'
 }`}
 >
 {duration} 秒
 </button>
 ))}
 </div>
 </section>

 <section>
 <div className="mb-2 flex items-center gap-2">
 <ImageIcon className="h-4 w-4 text-accent-500" />
 <span className="text-sm font-semibold text-ink-900">尺寸</span>
 </div>
 <div className="grid grid-cols-2 gap-2">
 {availableSizes.map(size => (
 <button
 key={size}
 onClick={() => setSelectedSize(size)}
 className={`rounded-2xl border px-3 py-3 text-left transition ${
 selectedSize === size
 ? 'border-accent-300 bg-accent-50 text-accent-700'
 : 'border-ink-200 text-ink-800 hover:border-accent-200 hover:bg-accent-50/40'
 }`}
 >
 <div className="text-sm font-semibold">{size}</div>
 <div className="mt-1 text-xs text-ink-500">
 {size.includes('x1280') || size.includes('x1792') ? '竖版' : '横版'}
 </div>
 </button>
 ))}
 </div>
 </section>

 <div className="rounded-2xl border border-accent-100 bg-accent-50 px-4 py-3">
 <div className="flex items-center gap-3">
 <Gem className="h-4 w-4 flex-shrink-0 text-accent-600" />
 <div className="text-sm font-semibold text-accent-900">
 消耗：¥{currentPrice.toFixed(1)}
 </div>
 </div>
 </div>

 <div className="flex gap-3">
 <button
 onClick={handleGenerate}
 disabled={!canGenerate}
 className={`flex-1 rounded-2xl px-4 py-3 text-sm font-semibold transition ${
 canGenerate
 ? 'bg-ink-900 text-white hover:bg-black'
 : 'cursor-not-allowed bg-cream-100 text-ink-400'
 }`}
 >
 <span className="flex items-center justify-center gap-2">
 {isGenerating ? (
 <>
 <RefreshCw className="h-4 w-4 animate-spin" />
 正在生成...
 </>
 ) : (
 <>
 <Sparkles className="h-4 w-4" />
 立即生成
 </>
 )}
 </span>
 </button>
 <button
 onClick={() => { setPrompt(''); setReferenceImage(null); }}
 className="rounded-2xl border border-ink-200 px-4 py-3 text-sm font-medium text-ink-700 transition hover:bg-cream-100"
 >
 清空
 </button>
 </div>

 </div>
 </div>
 </div>

 {/* ── 右栏：灰色内容区，画廊直接铺展 ── */}
 <div className="video-gallery flex-1 bg-cream-100 p-6">
 {generatedVideos.length > 0 ? (
 <div className="rounded-2xl bg-cream-50 p-5">
 <div className="mb-4 flex flex-col gap-3 border-b border-ink-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
 <div>
 <h2 className="text-lg font-semibold text-ink-900">生成结果</h2>
 <p className="mt-1 text-sm text-ink-500">已生成 {generatedVideos.length} 个视频</p>
 </div>
 <div className="inline-flex items-center gap-2 rounded-full bg-cream-100 px-3 py-1.5 text-xs text-ink-600">
 <SlidersHorizontal className="h-3.5 w-3.5" />
 左侧改参数，右侧看结果
 </div>
 </div>
 <div className="space-y-5">
 <div className="overflow-hidden rounded-2xl border border-ink-200 bg-ink-900 p-3">
 <div className="relative aspect-video overflow-hidden rounded-2xl bg-gradient-to-br from-ink-900 via-ink-800 to-green-900">
 <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(137,255,170,0.18),transparent_24%),radial-gradient(circle_at_75%_75%,rgba(126,180,255,0.16),transparent_25%)]" />
 <button className="absolute inset-0 flex items-center justify-center">
 <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/15 backdrop-blur">
 <Play className="ml-1 h-8 w-8 text-white" />
 </span>
 </button>
 <div className="absolute bottom-5 left-5 right-5 rounded-2xl border border-white/10 bg-black/25 px-4 py-3 text-white backdrop-blur">
 <div className="text-xs text-white/60">最新生成结果</div>
 <div className="mt-1 text-sm line-clamp-2">{generatedVideos[0].prompt}</div>
 </div>
 </div>
 </div>
 <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
 {generatedVideos.map(item => (
 <ResultCard key={item.id} item={item} />
 ))}
 </div>
 </div>
 </div>
 ) : (
 <ShowcaseGallery onUsePrompt={setPrompt} />
 )}
 </div>

 </div>

 <LoginModal
 isOpen={showLoginModal}
 onClose={closeLoginModal}
 onLogin={handleLoginSuccess}
 />
 </>
 );
}
